import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type { DocumentSummary } from "@/api/types";

export function useThemeVariants(sessionId?: string) {
  return useQuery({
    queryKey: ["docgen", "theme-variants", sessionId ?? null],
    queryFn: async () =>
      (
        await api.get<import("@/api/types").ThemeVariant[]>(
          sessionId
            ? `/docgen/theme-variants?session_id=${sessionId}`
            : "/docgen/theme-variants",
        )
      ).data,
    staleTime: 5 * 60_000,
  });
}

export function useDocuments(sessionId: string | undefined) {
  return useQuery({
    queryKey: keys.documents(sessionId ?? ""),
    queryFn: async () =>
      (await api.get<DocumentSummary[]>(`/sessions/${sessionId}/documents`))
        .data,
    enabled: Boolean(sessionId),
    // Event-driven, not polled. SessionView invalidates this query
    // when a ``document`` SSE event arrives on the active turn
    // stream (emitted by the internal render endpoint when docgen
    // MCP finalizes a draft). No idle requests.
  });
}

export type DocumentLibraryRow = DocumentSummary & {
  session_title: string;
  project_path: string | null;
  project_id: string | null;
  /** Context-only projects linked to the generating session (path
   *  format). Never includes the primary project_path. Empty when
   *  the session had no secondary context refs. */
  context_project_paths?: string[];
  /** Union of primary + context paths, de-duplicated, primary first.
   *  Pre-computed server-side so the library page can group by the
   *  exact project combination without re-fetching each session. */
  project_paths?: string[];
  shared: boolean;
  /** Email of the user who owns the generating session (i.e. who
   *  authored this document). Null on legacy rows where the join
   *  couldn't resolve. */
  author_email: string | null;
  author_display_name: string | null;
};

/** Documents the signed-in user can attach as a primary work file
 *  for a Modify-in-place session. Optionally filtered by
 *  ``artifact_format`` so the document_modify picker only shows
 *  DOCX rows and the presentation_modify picker only shows PPTX
 *  rows. Backed by the same ``GET /api/documents`` endpoint as the
 *  library page — share the auth + visibility rules — but without
 *  the enriched session/project columns the library needs.
 *
 *  Format param is sent on the wire as a query string so the
 *  backend can run the filter in SQL; on a small library the
 *  difference is negligible, but it keeps the contract explicit
 *  for future paging. */
export function useViewableDocuments(
  format?: "docx" | "pptx",
  /** List this org's documents instead of the app-bar org's: the new
   *  session's org, which may hold only its own documents. */
  org?: string | null,
) {
  const params: { format?: string; org?: string } = {};
  if (format) params.format = format;
  if (org) params.org = org;
  return useQuery({
    queryKey: ["viewable-documents", format ?? "all", org ?? null],
    queryFn: async () =>
      (
        await api.get<DocumentSummary[]>("/documents", {
          params: Object.keys(params).length > 0 ? params : undefined,
        })
      ).data,
    staleTime: 15_000,
  });
}

/** Every document owned by the current user, enriched with session
 *  title + project path so the library page can group without an
 *  extra session fetch per row. ``includeArchived=true`` adds rows
 *  the user has soft-deleted — the library's "Show archived" toggle
 *  flips this so archived rows surface alongside live ones. */
export function useDocumentLibrary({
  includeArchived = false,
  org,
  enabled = true,
}: {
  includeArchived?: boolean;
  /** List this org's documents instead of the app-bar org's. A session
   *  view passes its SESSION's org: the backend otherwise scopes the
   *  list by the active-org pin, which may name another org. */
  org?: string | null;
  enabled?: boolean;
} = {}) {
  const params: { archived?: string; org?: string } = {};
  if (includeArchived) params.archived = "true";
  if (org) params.org = org;
  return useQuery({
    queryKey: ["documents", "library", { includeArchived, org: org ?? null }],
    queryFn: async () =>
      (
        await api.get<DocumentLibraryRow[]>("/documents", {
          params: Object.keys(params).length > 0 ? params : undefined,
        })
      ).data,
    staleTime: 15_000,
    enabled,
  });
}

export function useExportDocument(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      template_slug: string;
      meta: Record<string, unknown>;
      blocks: Array<Record<string, unknown>>;
      parent_document_id?: string;
    }) =>
      (
        await api.post<DocumentSummary>(
          `/sessions/${sessionId}/documents`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.documents(sessionId) });
      qc.invalidateQueries({ queryKey: ["documents", "library"] });
    },
  });
}

/** Download the worktree's current modify-draft as a Blob.
 *
 *  Returns ``{blob, filename}`` so the caller can either pop a save
 *  dialog (anchor + ``download`` attribute) or hand the bytes to an
 *  external viewer. The endpoint is read-only — calling it doesn't
 *  promote the bytes anywhere.
 *
 *  Mutation rather than query because each click is an explicit
 *  user action; we don't want react-query to revalidate or share a
 *  cached blob between unrelated clicks. */
export function useDownloadWorkDraft(sessionId: string) {
  return useMutation({
    mutationFn: async (): Promise<{ blob: Blob; filename: string }> => {
      const resp = await api.get(`/sessions/${sessionId}/work-draft`, {
        responseType: "blob",
      });
      // Pull the suggested filename out of Content-Disposition so the
      // saved file mirrors the worktree's primary name without the
      // SPA needing to know it. Falls back to a sensible default if
      // the header was stripped by a proxy.
      const disposition =
        (resp.headers["content-disposition"] as string | undefined) ?? "";
      const match =
        /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
      const filename = match?.[1] ? decodeURIComponent(match[1]) : "draft";
      return { blob: resp.data as Blob, filename };
    },
  });
}

/** Promote the worktree's modify-draft to a real Document.
 *
 *  Server creates a new Document row, runs the LibreOffice DOCX/PPTX
 *  → PDF conversion, and stamps ``session.primary_document_id`` so the
 *  next turn picks up where the user saved. Invalidates both the
 *  per-session document list and the global library so the new row
 *  surfaces immediately.
 *
 *  Optional ``revision_notes`` lands on ``Document.summary`` and the
 *  audit row — useful when the user wants to annotate "what changed". */
export function usePersistWorkDraft(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: { revision_notes?: string | null } = {},
    ): Promise<DocumentSummary> =>
      (
        await api.post<DocumentSummary>(
          `/sessions/${sessionId}/work-draft/persist`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.documents(sessionId) });
      qc.invalidateQueries({ queryKey: ["documents", "library"] });
      // The session row's primary_document_id flips on success — the
      // SessionView reads it to decide whether to render "save again"
      // vs. "save as new", so refresh it too.
      qc.invalidateQueries({ queryKey: keys.session(sessionId) });
    },
  });
}

export function useDeleteDocument(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    // A bare id archives that version; ``scope: "lineage"`` archives
    // every version of the document in its session.
    mutationFn: async (
      arg: string | { documentId: string; scope?: "document" | "lineage" },
    ) => {
      const { documentId, scope } = typeof arg === "string" ? { documentId: arg, scope: undefined } : arg;
      await api.delete(`/documents/${documentId}`, {
        params: scope === "lineage" ? { scope } : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.documents(sessionId) });
      // Library holds cached copies under an ``includeArchived`` key
      // combination — invalidate the whole library queryspace so both
      // the live + archived views refresh.
      qc.invalidateQueries({ queryKey: ["documents", "library"] });
    },
  });
}

export function useRestoreDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) => {
      await api.post(`/documents/${documentId}/restore`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents", "library"] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useSetDocumentVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      documentId: string;
      visibility: "private" | "org";
      /** ``"lineage"``: every version of the document in its session. */
      scope?: "document" | "lineage";
    }) => {
      await api.post(`/documents/${payload.documentId}/visibility`, {
        visibility: payload.visibility,
        ...(payload.scope ? { scope: payload.scope } : {}),
      });
    },
    onSuccess: () => {
      // Visibility affects every listing (library + per-session).
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}
